// ログイン：Google Identity Services のボタン。開発時だけ「開発ログイン」（x-dev-user ヘッダー）。
// 初回はゲーム内の表示名を登録する（Googleの氏名・メールは使わない）。
import { needsPrologue } from './prologue';
import { api, ApiError, devAuth } from '../api';
import { go, register } from '../router';
import { applyMe, refreshMe, state } from '../state';
import { isLoaded, loadSheet, portraitHtml, portraitKey } from '../assets';
import { esc } from '../ui/dom';
import { errorText } from '../ui/status';
import { confirmDialog, toast } from '../ui/overlay';
import { onLoggedIn } from '../session';
import { renderGoogleButton } from '../ui/gsi';
import { avatarChoicesHtml } from '../ui/avatar';
import { prepareBgm } from '../audio';
import { icon } from '../ui/icons';
import { randomName } from '@hyakki/core';
import { openSoundDialog } from '../ui/sound';
import { trackSignup } from '../ui/x-pixel';
import { currentSource } from '../ui/source';

register('login', {
  title: 'ログイン',
  full: true,
  mount(el) {
    const needsName = !!state.me?.user.needsName;
    el.innerHTML = `
      <div class="login">
        <div class="login-bg" aria-hidden="true"></div>
        <div class="login-stage">
          <div class="login-figure" aria-hidden="true">${portraitHtml('akari', 'login-img')}</div>
          <div class="login-main">
            <h1 class="title-logo"><img src="/art/ui/logo.webp" alt="百鬼国盗" width="960" height="276"></h1>
            <div class="login-card panel-paper deco">
              <button type="button" class="login-bgm" aria-label="音設定">${icon('sound')}<span>音設定</span></button>
              <div class="login-body"></div>
              <p class="legal"><a href="/legal/terms.html" target="_blank" rel="noopener">利用規約</a>・<a href="/legal/privacy.html" target="_blank" rel="noopener">プライバシー</a>に同意のうえはじめてください。<br><a href="/legal/tokushoho.html" target="_blank" rel="noopener">特定商取引法に基づく表記</a></p>
            </div>
          </div>
        </div>
      </div>`;
    if (!isLoaded(portraitKey('akari'))) void loadSheet(portraitKey('akari')).then(() => {
      const f = el.querySelector('.login-figure');
      if (f) f.innerHTML = portraitHtml('akari', 'login-img');
    });
    // 音設定（全体・BGM・効果音）
    el.querySelector<HTMLButtonElement>('.login-bgm')!.addEventListener('click', () => openSoundDialog());
    const body = el.querySelector<HTMLElement>('.login-body')!;
    // はじめての人はこの後すぐプロローグ（「三百年前――」から曲を鳴らす）
    prepareBgm('story_mystery');
    if (needsName) return nameStep(body);
    body.innerHTML = `
      <div class="gsi-box main"><span class="gsi-reco" aria-hidden="true">＼おすすめ！／</span><div class="gsi-slot" aria-label="Googleではじめる"></div><p class="gsi-note muted small">ほかの端末でも同じデータで遊べます</p></div>
      <p class="guest-line"><button class="guest-start" type="button">ゲストではじめる</button><small>（第1章クリアまで・データはこの端末だけ）</small></p>
      ${
        import.meta.env.DEV
          ? `<details class="dev-login" open><summary>開発ログイン（ローカルのみ）</summary>
               <form class="row"><input name="id" value="${esc(devAuth.get() ?? 'dev1')}" pattern="[a-z0-9_\\-]{1,40}" aria-label="開発ユーザーID" required>
               <button class="btn">開発ログイン</button></form></details>`
          : ''
      }`;
    const note = body.querySelector<HTMLElement>('.gsi-note')!;
    const showGoogle = () =>
      void renderGoogleButton(body.querySelector('.gsi-slot')!, (c) => void signIn(c, body, showGoogle), 'continue_with').then((r) => {
        if (r === 'unconfigured') note.textContent = 'Googleログインは準備中です';
        else if (r === 'failed') note.textContent = 'Googleログインを読み込めませんでした。通信状況を確認して再読み込みしてください。';
      });
    showGoogle();
    const guestBtn = body.querySelector<HTMLButtonElement>('.guest-start')!;
    guestBtn.addEventListener('click', async () => {
      guestBtn.disabled = true;
      try {
        devAuth.set(null);
        if ((await api.guestLogin(currentSource())).created) trackSignup();
        await refreshMe();
        await afterLogin(body);
      } catch (e) {
        guestBtn.disabled = false;
        toast((e instanceof ApiError && e.status === 429 ? '短時間に多くのアカウントが作られました。しばらくしてからお試しください。' : errorText(e)), 'error', 4000);
      }
    });
    body.querySelector('.dev-login form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const id = String(new FormData(e.target as HTMLFormElement).get('id') ?? '').trim();
      devAuth.set(id);
      try {
        await refreshMe();
        await afterLogin(body);
      } catch (err) {
        // サーバー（wrangler dev）が動いていない：見本データで画面だけ確認できるようにする
        if (import.meta.env.DEV && err instanceof ApiError && (err.offline || err.status >= 500 || err.notReady)) {
          if (await confirmDialog('サーバーに接続できません。見本データで画面を確認しますか？（開発用・保存されません）', { ok: '見本で開く' })) {
            const { mockMe } = await import('../dev');
            sessionStorage.setItem('hk.mock', id);
            applyMe(mockMe(id));
            go('top', {}, true);
            return;
          }
        }
        devAuth.set(null);
        toast(errorText(err), 'error', 4000);
      }
    });
  },
});

async function signIn(credential: string, body: HTMLElement, retry: () => void) {
  try {
    if ((await api.googleLogin(credential, currentSource())).created) trackSignup();
    await refreshMe();
    await afterLogin(body);
  } catch (e) {
    if (e instanceof ApiError && e.code === 'invalid_token') {
      toast('Googleの確認に失敗しました。もう一度お試しください。', 'error', 4000);
      retry(); // 一度きりの値（nonce）を取り直してボタンを出し直す
    } else toast(errorText(e), 'error', 4000);
  }
}

async function afterLogin(body: HTMLElement) {
  // 初めてなら名前より先にプロローグ（名前は「三百年後」のあとで聞く）
  if (needsPrologue()) return go('prologue', {}, true);
  if (state.me?.user.needsName) return nameStep(body);
  await onLoggedIn();
  go('top', {}, true);
}

function nameStep(body: HTMLElement) {
  body.innerHTML = `
    <form class="name-form">
      <h2>表示名</h2>
      <p class="muted small">チャットと番付に表示されます。Googleの名前・メールアドレスは公開されません。</p>
      <div class="name-row-input">
        <input name="name" maxlength="12" minlength="2" required aria-label="表示名" value="${esc(randomName())}">
        <button type="button" class="icon-btn name-roll" data-roll aria-label="ほかの名前にする" title="ほかの名前にする">${icon('dice')}</button>
      </div>
      <p class="hint muted">2〜12文字。あとから設定で変えられます。</p>
      ${avatarChoicesHtml()}
      <button class="btn primary wide">はじめる</button>
    </form>`;
  let busy = false;
  // 名前の候補（data/random-names.ts）から引き直す
  body.querySelector('[data-roll]')!.addEventListener('click', () => {
    const input = body.querySelector<HTMLInputElement>('input[name="name"]')!;
    input.value = randomName(input.value);
  });
  body.querySelector('form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (busy) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const name = String(data.get('name') ?? '').trim();
    if (!name) return;
    busy = true;
    const controls = form.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, button');
    controls.forEach(c => c.disabled = true);
    try {
      if (state.me?.user.needsName) await api.setName(name, String(data.get('avatar')));
      await refreshMe();
      await onLoggedIn();
      go('top', {}, true);
    } catch (err) {
      toast(errorText(err), 'error', 4000);
    } finally {
      busy = false;
      controls.forEach(c => c.disabled = false);
    }
  });
}
